import { Component, OnInit, ViewChild, inject, TemplateRef, ChangeDetectionStrategy, ChangeDetectorRef, signal, effect, untracked } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { Role } from '@core/enums/roles';
import { ICourses } from '@core/interfaces/ilookup';
import { AuthService } from '@core/services/auth.service';
import { LookupService } from '@core/services/lookup.service';
import { map, switchMap, take, tap, catchError, finalize } from 'rxjs';
import { of } from 'rxjs';
import { CoursesService } from './services/courses.service';
import { ICourseAttachment } from './interface/icourse-attachment';
import { ICourseStatus } from './interface/icourse-status';
import { FormBuilder, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { ToastService } from '@core/services/toast.service';
import { DataService } from '@core/services/data.service';
import { NzModalService } from 'ng-zorro-antd/modal';
import { IUserCourses } from '@core/interfaces/iuser-courses';
import { InputType } from '@core/enums/input-type';
import { JournalService } from './services/journal.service';
import { SchoolYear } from '../report/enums/school-year';
import { ISchoolYear } from '@core/interfaces/ischool-year';
import { UserCourseService } from '../courses/services/user-courses.service';
import { CourseViewComponent } from './course-view/course-view.component';
import { environment } from '@env/environment';
@Component({
  selector: 'prism-course',
  templateUrl: './course.component.html',
  styleUrl: './course.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CourseComponent implements OnInit {
  @ViewChild(CourseViewComponent) courseViewComponent!: CourseViewComponent;
  @ViewChild('startCourseContent', { static: true })
  startCourseContent!: TemplateRef<any>;
  @ViewChild('timeUpContent', { static: true })
  timeUpContent!: TemplateRef<any>;

  private fb = inject(FormBuilder);
  private lookupService = inject(LookupService);
  private courseService = inject(CoursesService);
  private authService = inject(AuthService);
  private journalService = inject(JournalService);
  private sanitizer = inject(DomSanitizer);
  private router = inject(ActivatedRoute);
  private toastService = inject(ToastService);
  private dataService = inject(DataService);
  private modal = inject(NzModalService);
  private userCourseService = inject(UserCourseService);
  private cdr = inject(ChangeDetectorRef);
  private resourceContainer = 'courses';
  private attachmentContainer = 'course-attachments';
  courseTimeoutMinutes = environment.courseTimeoutMinutes;
  courseTimeoutSeconds = (environment.courseTimeoutMinutes || 60) * 60;
  courseStartTime: number | null = null;
  // Completed/reviewed on load: the course is view-only, so no minimum-stay timer.
  wasCompletedOnLoad = false;
  // The time-up reminder waits for the Session Expiring prompt to close so it isn't lost behind it.
  private timeUpReminderPending = signal(false);
  private timeUpReminderEffect = effect(() => {
    if (this.timeUpReminderPending() && !this.authService.sessionExpiringVisible()) {
      this.timeUpReminderPending.set(false);
      untracked(() => this.openTimeUpReminder());
    }
  }, { allowSignalWrites: true });
  courseSasToken: string = '';
  attachmentSasToken: string = '';
  course?: ICourses;
  attachments: ICourseAttachment[] = [];
  courseId = 0;
  userCourseId = 0;
  user = this.authService.getCurrentUser();
  role = Role;
  isAdmin = this.user?.roleId === Role.SystemAdmin;
  isEdit = false;
  startCourse = false;
  blobImgUrl: SafeResourceUrl = '';
  checked = false;
  completeForm: FormGroup | any;
  userCourse?: IUserCourses;
  hasCertificate = false;
  isDataLoading: boolean = true;
  isJournalModalOpen = false;
  private totalOperations: number = 0;
  private completedOperations: number = 0;
  InputType = InputType;
  btnText: string = 'Loading...';
  editCourseForm: FormGroup | any;
  addAttachment: boolean = false;
  courseName = 'course';
  allSectionsComplete = false;
  schoolYearId = SchoolYear.SY2026;
  isCourseCompleted = false;
  isLoadTester = false;
  isSubmitting = false;

  // Completed courses and any course from a previous school year show notebook entries read-only.
  get isNotebookReadOnly(): boolean {
    const isPreviousYear = !!this.userCourse?.schoolYearId && this.userCourse.schoolYearId !== this.schoolYearId;
    return this.wasCompletedOnLoad || this.hasCertificate || isPreviousYear;
  }

  get isCompleted(): AbstractControl | null { 
    return this.completeForm?.get('isCompleted'); 
  }

  private checkAllOperationsComplete(): void {
    this.completedOperations++;
    if (this.completedOperations === this.totalOperations) {
      this.isDataLoading = false;
      this.dataService.isLoading.set(false);
      this.cdr.markForCheck();
    }
  }

  ngOnInit(): void {
    this.isLoadTesting();
    this.getCurrentSchoolYear();
    this.router.params.subscribe(params => {
      this.courseId = +params['courseId'];
      this.userCourseId = params['userCourseId'] ? +params['userCourseId'] : 0;
      this.initForm();
    });
  }

  isLoadTesting(): void {
    this.isLoadTester = this.user?.isLoadTester;
  }

  initForm(): void {
    this.completeForm = this.fb.group({
      isCompleted: [false, Validators.required],
      courseId: [this.courseId, Validators.required]
    });

    this.dataService.isLoading.set(true);

    if (this.userCourseId) {
      this.totalOperations = 3; // getUserCourse + getSasTokenAndFetchCourses + getSasTokenAndFetchAttachments
      this.getUserCourse();
    } else {
      this.totalOperations = 3; // checkCourseStatus + getSasTokenAndFetchCourses + getSasTokenAndFetchAttachments
      this.checkCourseStatus();
      this.getSasTokenAndFetchCourses();
      this.getSasTokenAndFetchAttachments();
    }
  }

  checkCourseStatus(): void {
    this.courseService.getCourseStatus(this.courseId).pipe(
      take(1),
      tap((status: ICourseStatus) => {
        this.hasCertificate = status.earnedCertificate;
        this.isCompleted?.setValue(status.isCompleted ?? false);
        this.wasCompletedOnLoad = status.earnedCertificate || (status.isCompleted ?? false);
        this.btnText = (status.earnedCertificate || status.isCompleted) ? 'View Course' : 'Start Course';
      }),
      finalize(() => this.checkAllOperationsComplete())
    ).subscribe();
  }

  getSasTokenAndFetchAttachments(): void {
    this.authService.getStorageSasToken(this.attachmentContainer).pipe(
      take(1),
      tap((res: any) => {
        this.attachmentSasToken = res.value;
      }),
      switchMap(() => this.courseService.getAttachments(this.courseId).pipe(take(1))),
      tap((attachments: ICourseAttachment[]) => {
        this.attachments = attachments.map((attachment: ICourseAttachment) => {
          attachment.blobUrlSas = this.generateUrlWithSasToken(attachment.blobUrl, this.attachmentSasToken);
          return attachment; // Return the modified attachment
        });
      }),
      finalize(() => this.checkAllOperationsComplete())
    ).subscribe();
  }

  getSasTokenAndFetchCourses(): void {
    this.authService.getStorageSasToken(this.resourceContainer).pipe(
      take(1),
      tap((res: any) => {
        this.courseSasToken = res.value;
      }),
      switchMap(() => this.lookupService.getCourses().pipe(take(1))),
      tap((courses: ICourses[]) => {
        this.course = courses.find(course => course.id === this.courseId);

        if (this.course) {
          this.courseName = this.course.name;
          this.blobImgUrl = this.generateUrlWithSasToken(this.course?.thumbnail, this.courseSasToken);
        }
      }),
      finalize(() => this.checkAllOperationsComplete())
    ).subscribe();
  }

  getUserCourse(): void {
    if (!this.userCourseId) return;
    this.courseService.getUserCourse(this.userCourseId)
      .pipe(
        take(1),
        tap((userCourse: IUserCourses) => {
          this.userCourse = userCourse;
          this.courseName = userCourse?.course.name;
          this.course = userCourse?.course;
          if (userCourse?.courseId) {
            this.courseId = userCourse.courseId;
          }
          if (userCourse?.id) {
            this.userCourseId = userCourse.id;
          }

          this.checkCourseStatus();
          this.getSasTokenAndFetchCourses();
          this.getSasTokenAndFetchAttachments();
        }),
        finalize(() => this.checkAllOperationsComplete())
      ).subscribe();
  }

  getCurrentSchoolYear(): void {
    this.lookupService.getCurrentSchoolYear().pipe(
      take(1),
      tap((res: ISchoolYear) => { this.schoolYearId = res.id; })
    ).subscribe();
  }

  refreshCourseStatus(): void {
    this.courseService.getCourseStatus(this.courseId).pipe(take(1))
      .subscribe((status: ICourseStatus) => {
        this.hasCertificate = status.earnedCertificate;
        this.btnText = (status.earnedCertificate || status.isCompleted) ? 'View Course' : 'Start Course';
        this.cdr.markForCheck();
      });
  }

  generateUrlWithSasToken(blobUrl?: string, sasToken?: string) {
    if (sasToken && blobUrl) {
      const urlWithSasToken = `${blobUrl}${sasToken}`;
      return this.sanitizer.bypassSecurityTrustResourceUrl(urlWithSasToken);
    }
    return '';
  }

  toggleEdit(): void {
    this.isEdit = !this.isEdit;
    this.startCourse = false;
  }

  toggleStartCourse(): void {
    if (this.hasCertificate || this.isCompleted?.value) {
      this.startCourse = !this.startCourse;
      this.isEdit = false;
      return;
    }

    this.modal.confirm({
      nzContent: this.startCourseContent,
      nzOkText: 'OK',
      nzIconType: '',
      nzClosable: false,
      nzCancelText: null,
      nzOnOk: () => {
        this.startCourse = true;
        this.isEdit = false;
        this.cdr.markForCheck();

        this.addUserCourse();
      }
    });
  }

  viewPrintCertificate(): void {
    this.courseService.downloadCertificate(this.courseId).pipe(
      take(1)
    ).subscribe(
      (blob: any) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'Certificate.pdf';
        link.click();
        window.URL.revokeObjectURL(url);
      }
    );
  }

  addUserCourse(): void {
    this.courseService.addUserCourse(this.courseId).pipe(
      tap((res: boolean) => {
        if (res) {
          this.refreshCourseStatus();
        }
      }),
      catchError((err) => {
        console.error('addUserCourse error:', err);
        return of(false);
      })
    ).subscribe();
  }

  handleAllComplete(isAllComplete: boolean): void {
    this.allSectionsComplete = isAllComplete;
  }

  handleCourseStarted(startTime: number): void {
    this.courseStartTime = startTime;
    this.cdr.markForCheck();
  }

  // Minimum stay met: remind them how to finish. OK only closes, so they stay on the course.
  showTimeUpReminder(): void {
    this.timeUpReminderPending.set(true);
  }

  private openTimeUpReminder(): void {
    this.modal.confirm({
      nzContent: this.timeUpContent,
      nzOkText: 'OK',
      nzIconType: '',
      nzClosable: false,
      nzCancelText: null
    });
  }

  handleCourseCompleted(isCompleted: boolean): void {
    this.isCourseCompleted = isCompleted;
  }

  handleCheckbox(): void {
    const currentValue = this.isCompleted?.value;
    if (currentValue !== undefined) {
      this.isCompleted?.setValue(!currentValue);
    }

    if (this.courseId > 0) {
      this.courseService.acknowledgeCourse(this.courseId).pipe(take(1)).subscribe({
        error: () => {
          this.toastService.error('Unable to save your acknowledgment. Please try again.');
        }
      });
    }
  }

  submit() {
    if (this.isSubmitting) return;

    // for load testing purposes
    if (!this.isLoadTester) {
      if (!this.isCompleted?.value) {
        this.toastService.error('Please agree to the terms and conditions to continue');
        return;
      }

      // Check if checkbox is checked (terms and conditions)
      if (!this.isCompleted?.value) {
        this.toastService.error('Please agree to the terms and conditions to continue');
        return;
      }

      // Check: Prevent duplicate submissions (optional)
      if (this.userCourse?.isCompleted) {
        this.toastService.error('Course is already completed.');
        return;
      }
    }

    this.isSubmitting = true;

    this.journalService.getJournalSections(this.courseId).pipe(
      take(1),
      switchMap(sections => {
        return this.journalService.getByCourseId(this.courseId).pipe(
          take(1),
          map(entries => {
            const total = sections.length;
            const completed = sections.filter(section =>
              entries.some(entry => entry.journalSectionId === section.id)
            ).length;

            return { isAllComplete: completed === total, total, completed };
          })
        );
      })
    ).subscribe({
      next: (result) => {
        if (!result.isAllComplete && !this.isLoadTester) {
          this.toastService.error(`Please complete all Notebook Entry sections to continue (${result.completed}/${result.total} completed)`);
          this.isSubmitting = false;
          return;
        }

        if (this.courseId > 0) {
          this.courseService.completeCourse(this.courseId).pipe(take(1)).subscribe({
            next: (res: boolean) => {
              if (res) {
                this.startCourse = false;
                this.isEdit = false;
                this.btnText = 'View Course';
                this.toastService.success('Course completed.');
                this.dataService.isLoading.set(true);
                this.refreshCourseStatus();
                this.cdr.markForCheck();
                if (this.userCourseId) {
                  this.getUserCourse();
                }
                return;
              }

              this.toastService.error('Unable to complete course. Please try again.');
              this.isSubmitting = false;
            },
            error: () => {
              this.toastService.error('Unable to complete course. Please try again.');
              this.isSubmitting = false;
            }
          });
        } else {
          this.isSubmitting = false;
        }
      },
      error: () => {
        this.isSubmitting = false;
      }
    });
  }

  updateAttachments(): void {
    this.getSasTokenAndFetchAttachments();
  }

  updateCourse(): void {
    this.isEdit = false;
    this.getSasTokenAndFetchCourses();
    this.getSasTokenAndFetchAttachments();
  }

  handleKeydown(event: KeyboardEvent) {
    if (this.isSubmitting) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.submit();
    }
  }

  openDigitalNotebook(): void {
    this.courseViewComponent?.showModal();
  }
}
